import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import Modal from "../../components/modal";
import Alert from "../../components/alert";
import { Select } from "../../components/input-functions";
import MultiSelect, {
  MultiSelectOption,
} from "../../components/searchable-multi-select";
import {
  CreatePolicyAttachmentRequest,
  PolicyAttachmentCreationOptions,
  ApplicabilityStrategy,
  PolicyDiffMapping,
  UnderwritingPolicyAttachment,
  UnderwritingPolicyAttachmentStatus,
} from "../../models/underwritingPolicyAttachment";
import { Segment } from "../../models/segment";
import dispatch from "../../middleware";
import {
  createPolicyAttachmentInShadow,
  getPolicyAttachments,
} from "../../actions/policy-attachment.action";
import { getBboxFinderUrl, getGoogleMapsPointUrl } from "./underwritingUtils";
import UnderwritingUiCompareLink from "../underwriting-ui/UnderwritingUiCompareLink";

interface CreatePolicyAttachmentModalProps {
  isOpen: boolean;
  creationOptions: PolicyAttachmentCreationOptions;
  segments: Segment[];
  creationOptionsNotAuthorized?: boolean;
  segmentsNotAuthorized?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const initialFormData: CreatePolicyAttachmentRequest = {
  employerId: "",
  policies: [],
  description: "",
};

export default function CreatePolicyAttachmentModal({
  isOpen,
  creationOptions,
  segments,
  creationOptionsNotAuthorized = false,
  segmentsNotAuthorized = false,
  onClose,
  onSuccess,
}: CreatePolicyAttachmentModalProps) {
  const storeDispatch = useDispatch();
  const [formData, setFormData] =
    useState<CreatePolicyAttachmentRequest>(initialFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [activeAttachment, setActiveAttachment] =
    useState<UnderwritingPolicyAttachment | null>(null);
  const [isFetchingActive, setIsFetchingActive] = useState(false);

  useEffect(() => {
    if (!formData.employerId) {
      setActiveAttachment(null);
      setFormData((prev) => ({ ...prev, policyDiffMappings: undefined }));
      return;
    }

    setIsFetchingActive(true);
    dispatch(
      storeDispatch,
      getPolicyAttachments({
        employerIds: [formData.employerId],
        statusList: [UnderwritingPolicyAttachmentStatus.ACTIVE],
        limit: 1,
      })
    )
      .then((attachments: UnderwritingPolicyAttachment[]) => {
        const active = attachments[0] ?? null;
        setActiveAttachment(active);
        setFormData((prev) => ({ ...prev, policyDiffMappings: undefined }));
      })
      .catch(() => setActiveAttachment(null))
      .finally(() => setIsFetchingActive(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.employerId]);

  useEffect(() => {
    if (!activeAttachment || formData.policies.length === 0) {
      return;
    }
    const newMappings: PolicyDiffMapping[] = formData.policies.map(
      (policy) => ({
        newPolicy: policy,
        oldPolicy:
          formData.policyDiffMappings?.find((m) => m.newPolicy === policy)
            ?.oldPolicy ?? "",
      })
    );
    setFormData((prev) => ({ ...prev, policyDiffMappings: newMappings }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.policies, activeAttachment]);

  async function handleSubmit() {
    setError("");

    if (!formData.employerId || formData.policies.length === 0) {
      setError("Employer ID and at least one policy are required");
      return;
    }

    if (
      formData.applicabilityMetadata?.strategy ===
        ApplicabilityStrategy.SEGMENT &&
      !formData.applicabilityMetadata?.segmentId
    ) {
      setError("Segment ID is required when strategy is SEGMENT");
      return;
    }

    if (
      formData.applicabilityMetadata?.strategy ===
      ApplicabilityStrategy.GEOLOCATION
    ) {
      const geoConfig = formData.applicabilityMetadata.geoConfig;
      if (!geoConfig?.maxStaleDurationHours || geoConfig.maxStaleDurationHours < 1) {
        setError("Max stale duration is required when strategy is GEOLOCATION");
        return;
      }
      if (!geoConfig.circle && !geoConfig.boundingBox) {
        setError("Circle or bounding box is required when strategy is GEOLOCATION");
        return;
      }
    }

    const diffMappingsToSubmit = formData.policyDiffMappings
      ?.filter((m) => m.oldPolicy)
      .map(({ newPolicy, oldPolicy }) => ({ newPolicy, oldPolicy }));

    try {
      setIsLoading(true);
      await dispatch(
        storeDispatch,
        createPolicyAttachmentInShadow({
          ...formData,
          policyDiffMappings:
            diffMappingsToSubmit && diffMappingsToSubmit.length > 0
              ? diffMappingsToSubmit
              : undefined,
        })
      );
      setFormData(initialFormData);
      setActiveAttachment(null);
      onSuccess();
    } catch (error: any) {
      setError(error.message || "Failed to create policy attachment");
    } finally {
      setIsLoading(false);
    }
  }

  function handleClose() {
    setFormData(initialFormData);
    setActiveAttachment(null);
    setError("");
    onClose();
  }

  function handleInputChange(
    field: keyof CreatePolicyAttachmentRequest,
    value: any
  ) {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function handleApplicabilityMetadataChange(field: string, value: any) {
    setFormData((prev) => ({
      ...prev,
      applicabilityMetadata: {
        ...prev.applicabilityMetadata!,
        [field]: value,
      },
    }));
  }

  function handleStrategyChange(strategy: ApplicabilityStrategy) {
    setFormData((prev) => ({
      ...prev,
      applicabilityMetadata: strategy
        ? {
          strategy,
          userIdSuffixes: [],
          segmentId: undefined,
          geoConfig:
            strategy === ApplicabilityStrategy.GEOLOCATION
              ? {
                  maxStaleDurationHours: 24,
                  circle: { lat: 0, lng: 0, radiusMetres: 1 },
                }
              : undefined,
        }
        : undefined,
    }));
  }

  function handleGeoShapeChange(shape: "circle" | "boundingBox") {
    const maxStaleDurationHours =
      formData.applicabilityMetadata?.geoConfig?.maxStaleDurationHours ?? 24;
    handleApplicabilityMetadataChange(
      "geoConfig",
      shape === "circle"
        ? {
            maxStaleDurationHours,
            circle: { lat: 0, lng: 0, radiusMetres: 1 },
          }
        : {
            maxStaleDurationHours,
            boundingBox: { minLat: 0, maxLat: 0, minLng: 0, maxLng: 0 },
          }
    );
  }

  function handleGeoConfigChange(path: string, value: number) {
    setFormData((prev) => {
      const geoConfig = prev.applicabilityMetadata?.geoConfig ?? {
        maxStaleDurationHours: 24,
      };
      const nextGeoConfig: any = { ...geoConfig };
      const parts = path.split(".");
      if (parts.length === 1) {
        nextGeoConfig[parts[0]] = value;
      } else {
        nextGeoConfig[parts[0]] = {
          ...(nextGeoConfig[parts[0]] ?? {}),
          [parts[1]]: value,
        };
      }
      return {
        ...prev,
        applicabilityMetadata: {
          ...prev.applicabilityMetadata!,
          geoConfig: nextGeoConfig,
        },
      };
    });
  }

  function handleUserIdSuffixesChange(value: string) {
    const suffixes = value
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s);
    handleApplicabilityMetadataChange("userIdSuffixes", suffixes);
  }

  function handleDiffMappingChange(newPolicy: string, oldPolicy: string) {
    setFormData((prev) => ({
      ...prev,
      policyDiffMappings: prev.policyDiffMappings?.map((m) =>
        m.newPolicy === newPolicy ? { ...m, oldPolicy } : m
      ),
    }));
  }

  function renderPolicyDiffMappings() {
    if (!activeAttachment || formData.policies.length === 0) return null;

    const oldPolicyOptions = [
      { value: "", label: "New (no comparison)" },
      ...activeAttachment.policies.map((p) => ({ value: p, label: p })),
    ];

    return (
      <div className="col-12 mb-3">
        <h6>Policy Diff Mappings</h6>
        <p className="text-muted small mb-2">
          Current active attachment: <strong>{activeAttachment._id}</strong> has{" "}
          {activeAttachment.policies.length} polic
          {activeAttachment.policies.length === 1 ? "y" : "ies"}. Map each new
          policy to the old one it replaces for diff tracking.
        </p>
        <table
          className="table table-sm table-bordered"
          style={{ tableLayout: "fixed", width: "100%" }}
        >
          <colgroup>
            <col style={{ width: "45%" }} />
            <col style={{ width: "40%" }} />
            <col style={{ width: "15%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>New Policy</th>
              <th>Replaces (Old Policy)</th>
              <th>Preview</th>
            </tr>
          </thead>
          <tbody>
            {formData.policyDiffMappings?.map(({ newPolicy, oldPolicy }) => (
              <tr key={newPolicy}>
                <td style={{ wordBreak: "break-all" }}>
                  <small>{newPolicy}</small>
                </td>
                <td style={{ overflow: "hidden" }}>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "100%", minWidth: 0 }}
                    value={oldPolicy}
                    onChange={(e) =>
                      handleDiffMappingChange(newPolicy, e.target.value)
                    }
                  >
                    {oldPolicyOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {oldPolicy ? (
                    <UnderwritingUiCompareLink
                      basePolicy={oldPolicy}
                      targetPolicy={newPolicy}
                    >
                      View Diff ↗
                    </UnderwritingUiCompareLink>
                  ) : (
                    <span className="text-muted small">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const policyOptions: MultiSelectOption[] =
    creationOptions.availablePolicies.map((policy) => ({
      value: policy.name,
      text: policy.name,
      html: `<strong>${policy.name}</strong><br><small style="color: #666;">${policy.description}</small>`,
    }));

  const employerOptions: MultiSelectOption[] = creationOptions.employers.map(
    (employer) => ({
      value: employer._id,
      text: `${employer.name} - ${employer._id}`,
    })
  );

  const segmentOptions: MultiSelectOption[] = segments.map((segment) => ({
    value: segment._id,
    text: `${segment.name} — ${segment._id}`,
    html: `
      <div style="line-height:1.6">
        <strong>${segment.name}</strong>
        <br/>
        <small style="color:#555">${segment._id}</small>
        <br/>
        <small style="color:#888">By: ${segment.createdBy}</small>
      </div>
    `,
  }));

  const selectedStrategy = formData.applicabilityMetadata?.strategy;
  const selectedSegmentId = formData.applicabilityMetadata?.segmentId;
  const circle = formData.applicabilityMetadata?.geoConfig?.circle;
  const boundingBox = formData.applicabilityMetadata?.geoConfig?.boundingBox;
  const googleMapsPointUrl = getGoogleMapsPointUrl(circle);
  const bboxFinderUrl = getBboxFinderUrl(boundingBox);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title="Create Policy Attachment"
      hasSubmitButton={true}
      submitButtonText="Create in Shadow Mode"
      onSubmit={handleSubmit}
      hasSubmitButtonLoading={isLoading}
      hasCancelButton={true}
      cancelButtonText="Cancel"
      size="large"
    >
      <div>
        <div className="row">
          {creationOptionsNotAuthorized ? (
            <div className="col-12 mb-3">
              <Alert type="danger" message="Not authorized to load creation options." />
            </div>
          ) : (
          <>
          <div className="col-12 mb-3">
            <label className="form-label">Employer *</label>
            <MultiSelect
              options={employerOptions}
              placeholder="Select Employer"
              value={formData.employerId ? [formData.employerId] : []}
              onChange={(values) =>
                handleInputChange("employerId", values[0] || "")
              }
              search
              selectAll={false}
              maxSelections={1}
            />
          </div>

          <div className="col-12 mb-3">
            <label className="form-label">Policies *</label>
            <MultiSelect
              options={policyOptions}
              placeholder="Select policies"
              value={formData.policies}
              onChange={(values) => handleInputChange("policies", values)}
              search
              selectAll={false}
              minSelections={1}
              showBadges
            />
          </div>
          </>
          )}

          <div className="col-12 mb-3">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Optional description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
            />
          </div>

          {isFetchingActive && (
            <div className="col-12 mb-2">
              <small className="text-muted">
                Loading current active attachment...
              </small>
            </div>
          )}

          {!isFetchingActive && formData.employerId && !activeAttachment && (
            <div className="col-12 mb-2">
              <small className="text-muted">
                No active attachment found for this employer. This will be a new
                attachment.
              </small>
            </div>
          )}

          {renderPolicyDiffMappings()}

          <div className="col-12 mb-3">
            <h6>Applicability Metadata (Optional)</h6>
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">Strategy</label>
            <Select
              placeholder="Select Strategy"
              options={[
                {
                  label: "User ID Suffix",
                  value: ApplicabilityStrategy.USER_ID_SUFFIX,
                },
                {
                  label: "Segment",
                  value: ApplicabilityStrategy.SEGMENT,
                },
                {
                  label: "Geolocation",
                  value: ApplicabilityStrategy.GEOLOCATION,
                },
              ]}
              value={selectedStrategy}
              onChange={(e) =>
                handleStrategyChange(e.target.value as ApplicabilityStrategy)
              }
            />
          </div>

          {selectedStrategy === ApplicabilityStrategy.USER_ID_SUFFIX && (
            <div className="col-md-6 mb-3">
              <label className="form-label">
                User ID Suffixes (comma-separated)
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter user ID suffixes separated by commas"
                onChange={(e) => handleUserIdSuffixesChange(e.target.value)}
              />
            </div>
          )}

          {selectedStrategy === ApplicabilityStrategy.SEGMENT && (
            <div className="col-md-6 mb-3">
              <label className="form-label">Segment *</label>
              {segmentsNotAuthorized ? (
                <Alert type="danger" message="Not authorized to load segments." />
              ) : (
                <MultiSelect
                  options={segmentOptions}
                  placeholder="Select Segment"
                  value={selectedSegmentId ? [selectedSegmentId] : []}
                  onChange={(values) =>
                    handleApplicabilityMetadataChange(
                      "segmentId",
                      values[0] || undefined
                    )
                  }
                  search
                  selectAll={false}
                  maxSelections={1}
                />
              )}
            </div>
          )}

          {selectedStrategy === ApplicabilityStrategy.GEOLOCATION && (
            <div className="col-12 mb-3">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Match Window (hours) *</label>
                  <input
                    type="number"
                    min={1}
                    className="form-control"
                    value={
                      formData.applicabilityMetadata?.geoConfig
                        ?.maxStaleDurationHours ?? ""
                    }
                    onChange={(e) =>
                      handleGeoConfigChange(
                        "maxStaleDurationHours",
                        Number(e.target.value)
                      )
                    }
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Geofence Shape *</label>
                  <select
                    className="form-select"
                    value={
                      formData.applicabilityMetadata?.geoConfig?.boundingBox
                        ? "boundingBox"
                        : "circle"
                    }
                    onChange={(e) =>
                      handleGeoShapeChange(
                        e.target.value as "circle" | "boundingBox"
                      )
                    }
                  >
                    <option value="circle">Circle</option>
                    <option value="boundingBox">Bounding Box</option>
                  </select>
                </div>
              </div>
              {formData.applicabilityMetadata?.geoConfig?.circle && (
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Center Latitude *</label>
                    <input type="number" step="any" className="form-control" value={formData.applicabilityMetadata.geoConfig.circle.lat} onChange={(e) => handleGeoConfigChange("circle.lat", Number(e.target.value))} />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Center Longitude *</label>
                    <input type="number" step="any" className="form-control" value={formData.applicabilityMetadata.geoConfig.circle.lng} onChange={(e) => handleGeoConfigChange("circle.lng", Number(e.target.value))} />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Radius (metres) *</label>
                    <input type="number" min={1} className="form-control" value={formData.applicabilityMetadata.geoConfig.circle.radiusMetres} onChange={(e) => handleGeoConfigChange("circle.radiusMetres", Number(e.target.value))} />
                  </div>
                  <div className="col-12 mb-3">
                    <a href={googleMapsPointUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                      View point in Google Maps ↗
                    </a>
                  </div>
                </div>
              )}
              {formData.applicabilityMetadata?.geoConfig?.boundingBox && (
                <div className="row">
                  <div className="col-md-3 mb-3">
                    <label className="form-label">Min Latitude *</label>
                    <input type="number" step="any" className="form-control" value={formData.applicabilityMetadata.geoConfig.boundingBox.minLat} onChange={(e) => handleGeoConfigChange("boundingBox.minLat", Number(e.target.value))} />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label">Max Latitude *</label>
                    <input type="number" step="any" className="form-control" value={formData.applicabilityMetadata.geoConfig.boundingBox.maxLat} onChange={(e) => handleGeoConfigChange("boundingBox.maxLat", Number(e.target.value))} />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label">Min Longitude *</label>
                    <input type="number" step="any" className="form-control" value={formData.applicabilityMetadata.geoConfig.boundingBox.minLng} onChange={(e) => handleGeoConfigChange("boundingBox.minLng", Number(e.target.value))} />
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label">Max Longitude *</label>
                    <input type="number" step="any" className="form-control" value={formData.applicabilityMetadata.geoConfig.boundingBox.maxLng} onChange={(e) => handleGeoConfigChange("boundingBox.maxLng", Number(e.target.value))} />
                  </div>
                  <div className="col-12 mb-3">
                    <a href={bboxFinderUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                      View bounding box in bboxfinder ↗
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
